import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/articles';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <div className="w-8 h-8 md:w-9 md:h-9 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-background-50 font-bold text-xs md:text-sm font-heading">911</span>
            </div>
            <span className={`font-heading font-bold text-lg md:text-xl transition-colors ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}>
              爆料网
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : isScrolled
                  ? 'text-foreground-700 hover:text-primary-500'
                  : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              首页
            </Link>

            {/* Categories Dropdown */}
            <div className="relative group">
              <Link
                to="/category/wanghong-heiliao"
                className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500'
                    : isScrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                分类
                <i className="ri-arrow-down-s-line text-xs"></i>
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-background-50 rounded-lg border border-background-200/70 py-2 min-w-[160px]">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-500'
                  : isScrolled
                  ? 'text-foreground-700 hover:text-primary-500'
                  : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'text-foreground-600 hover:text-primary-500 hover:bg-background-200'
                  : 'text-background-50/80 hover:text-background-50 hover:bg-white/10'
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            {/* Subscribe CTA */}
            <Link
              to="/about"
              className={`hidden md:flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                isScrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50/20 text-background-50 border border-background-50/30 hover:bg-background-50/30'
              }`}
            >
              订阅追踪
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`md:hidden w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'text-foreground-600'
                  : 'text-background-50'
              }`}
              aria-label="菜单"
            >
              <i className={`text-lg ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div className={`pb-3 ${isScrolled ? 'border-t border-background-200/70 pt-3' : 'pt-2'}`}>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索爆料..."
                className="w-full pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-300 rounded-full text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/70 pt-3">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`px-3 py-2.5 rounded-lg text-sm font-medium ${
                  location.pathname === '/'
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                首页
              </Link>
              <div className="px-3 py-2 text-xs text-foreground-400 font-medium">内容分类</div>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100"
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`px-3 py-2.5 rounded-lg text-sm font-medium ${
                  location.pathname === '/about'
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}